import {
  CalendarDateTime,
  getLocalTimeZone,
  toCalendarDateTime,
  toZoned,
} from "@internationalized/date";
import { format } from "date-fns";
import { GlobalEventResponse } from "features/CenterContent/RoleContent/GlobalEvents/types/responseSchema";
import { InternalMessageResponse } from "features/CenterContent/RoleContent/InternalMessage/schema/responseSchema";
import { EventCategory } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/Badges";
import {
  Duration,
  durationSchema,
  InfrastructureResponse,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/responseSchema";
import { Stop } from "features/CenterContent/VehicleDetails/TrainDetails/useExistingTrainCancelledEvents";
import { Tooltip } from "shared/components/Spor";
import { VisibilityResponse } from "shared/form/visibilitySchema";
import { partition } from "shared/utils/arrayUtils";
import { calendarDateTimeToDate as toDate } from "shared/utils/dateUtils";

export const getDisplayDurationFromVisibility = (
  visibility: VisibilityResponse,
): DisplayDuration | null => {
  switch (visibility.type) {
    case "TIMED":
      return {
        fromTime: toCalendarDateTime(visibility.fromTime),
        toTime: toCalendarDateTime(visibility.toTime),
      };
    case "OPEN_ENDED":
      return {
        fromTime: toCalendarDateTime(visibility.fromTime),
        toTime: null,
      };
    case "CONSTANT":
      return null;
  }
};

export type DisplayDuration = {
  fromTime: CalendarDateTime;
  toTime: CalendarDateTime | null;
};

export const getDisplayDuration = (
  duration: Duration,
): DisplayDuration | null => {
  switch (duration.type) {
    case "TIMED":
      return {
        fromTime: duration.from_time,
        toTime: duration.to_time,
      };
    case "OPEN_ENDED":
      return {
        fromTime: duration.from_time,
        toTime: null,
      };
    case "PERIODIC": {
      if (duration.periods.length === 0) return null;

      const from = duration.periods.sort(
        (a, b) => toDate(a.from_time).valueOf() - toDate(b.from_time).valueOf(),
      )[0];
      const to = duration.periods.sort(
        (a, b) => toDate(b.to_time).valueOf() - toDate(a.to_time).valueOf(),
      )[0];

      return {
        fromTime: from.from_time,
        toTime: to.to_time,
      };
    }
  }
};

export const sortByDisplayDuration = (
  a: InfrastructureResponse,
  b: InfrastructureResponse,
) => {
  const durationA = getDisplayDuration(a.durations);
  const durationB = getDisplayDuration(b.durations);

  if (!durationA || !durationB) return 0;

  const fromTimeA = toDate(durationA.fromTime).valueOf();
  const fromTimeB = toDate(durationB.fromTime).valueOf();

  return fromTimeA - fromTimeB;
};

export const getDisplayDurationString = (
  displayDuration: DisplayDuration | null,
) => {
  if (displayDuration === null) return "Ukjent tidsrom.";

  if (!displayDuration.toTime)
    return `Fra kl. ${format(toDate(displayDuration.fromTime), "HH:mm, dd.MM.yyyy")}`;

  return `Fra ${format(toDate(displayDuration.fromTime), "dd.MM.yyyy")} - ${format(
    toDate(displayDuration.toTime),
    "dd.MM.yyyy",
  )}`;
};

export const sortByVisibility = (
  a: GlobalEventResponse,
  b: GlobalEventResponse,
): number => {
  if (a.visibility.type === "CONSTANT" || b.visibility.type === "CONSTANT") {
    return 0;
  }
  const fromTimeA = toZoned(a.visibility.fromTime, getLocalTimeZone())
    .toDate()
    .valueOf();
  const fromTimeB = toZoned(b.visibility.fromTime, getLocalTimeZone())
    .toDate()
    .valueOf();

  return fromTimeB - fromTimeA;
};

export const getCategory = (event: InternalMessageResponse): EventCategory => {
  if (event.relevantTrain) return "train";
  if (event.relevantStops.length > 0) return "station";

  const category = event.relatedEventDetails?.category;
  switch (category) {
    case "TRAIN":
      return "trainEvent";
    case "STATION":
      return "stationEvent";
    case "INFRASTRUCTURE":
      return "infrastructure";
    case "GLOBAL":
      return "global";

    case undefined:
      return "internal";
  }
};

const getStopName = (stop: Stop) => stop?.ids[0]?.name ?? stop?.ids[0]?.id;

export const getBadgeItem = (
  event: InternalMessageResponse,
): string | undefined => {
  const identifier = event.relevantTrain?.identifier;
  return identifier ?? getStopName(event.relevantStops[0]);
};

export const getBadgeExtra = (event: InternalMessageResponse) => {
  const numStops = event.relevantStops.length;
  if (numStops <= 1) return null;
  return (
    <Tooltip
      label={event.relevantStops
        .slice(1)
        .map((it) => getStopName(it))
        .join(", ")}
      hasArrow
    >
      +{numStops - 1}
    </Tooltip>
  );
};

export const partitionInternalMessagesRelatedToEvent = (
  data: InternalMessageResponse[],
) => partition(data, (internalMessage) => !internalMessage.relatedEventUuid);

export const getDisplayDurationStringFromUnparsedResponse = (
  unparsedResponse: InfrastructureResponse,
) => {
  try {
    const durations = durationSchema.parse(unparsedResponse.durations);
    return getDisplayDurationString(getDisplayDuration(durations));
  } catch {
    return "Fikk ikke hentet dato";
  }
};
